// Viewer-count timeline helpers for the inline SVG charts. Pure functions.

const MOMENT_TYPES = ["join", "leave", "tip", "follow", "share", "request"];

// Accepts [{minute|t|time, viewers|count|value}] or a plain number[].
export function normalizeTimeline(timeline) {
  if (!Array.isArray(timeline)) return [];
  return timeline
    .map((p, i) => {
      if (typeof p === "number") return { minute: i, viewers: p };
      if (!p || typeof p !== "object") return null;
      const minute = Number(p.minute ?? p.t ?? p.time ?? p.x ?? i);
      const viewers = Number(p.viewers ?? p.count ?? p.value ?? p.y ?? 0);
      if (!Number.isFinite(minute)) return null;
      return { minute, viewers: Number.isFinite(viewers) ? viewers : 0 };
    })
    .filter(Boolean)
    .sort((a, b) => a.minute - b.minute);
}

// Collapses a long per-minute series into at most `maxBuckets` averaged points.
export function bucketTimeline(points, maxBuckets = 60) {
  const series = normalizeTimeline(points);
  if (series.length <= maxBuckets || maxBuckets < 1) return series;
  const size = Math.ceil(series.length / maxBuckets);
  const out = [];
  for (let i = 0; i < series.length; i += size) {
    const slice = series.slice(i, i + size);
    const viewers = slice.reduce((sum, p) => sum + p.viewers, 0) / slice.length;
    out.push({ minute: slice[0].minute, viewers: Math.round(viewers * 10) / 10 });
  }
  return out;
}

// Converts moments to minute offsets on the timeline. A moment may carry
// `minute` directly, or a timestamp (`ts|at|createdAt`) relative to startedAt.
export function momentTicks(moments, startedAt, totalMinutes) {
  if (!Array.isArray(moments)) return [];
  const start = startedAt ? new Date(startedAt).getTime() : NaN;
  const ticks = [];
  for (const m of moments) {
    if (!m) continue;
    const type = String(m.type || m.kind || "").toLowerCase();
    if (!MOMENT_TYPES.includes(type)) continue;
    let minute = Number(m.minute);
    if (!Number.isFinite(minute)) {
      const ts = new Date(m.ts ?? m.at ?? m.createdAt ?? m.timestamp).getTime();
      if (Number.isNaN(ts) || Number.isNaN(start)) continue;
      minute = (ts - start) / 60000;
    }
    if (minute < 0) continue;
    if (Number.isFinite(totalMinutes) && totalMinutes > 0 && minute > totalMinutes) continue;
    ticks.push({ minute, type });
  }
  return ticks.sort((a, b) => a.minute - b.minute);
}

export function countByType(ticks) {
  const counts = {};
  for (const t of ticks) counts[t.type] = (counts[t.type] || 0) + 1;
  return counts;
}

// Maps series points into SVG space; returns { path, scaleX, scaleY, maxY, minX, maxX }.
export function linePath(points, width, height, pad = 4) {
  const series = normalizeTimeline(points);
  if (series.length === 0) {
    return { path: "", scaleX: () => pad, scaleY: () => height - pad, maxY: 0, minX: 0, maxX: 0 };
  }
  const minX = series[0].minute;
  const maxX = series[series.length - 1].minute;
  const maxY = Math.max(1, ...series.map((p) => p.viewers));
  const spanX = maxX - minX || 1;
  const innerW = Math.max(1, width - pad * 2);
  const innerH = Math.max(1, height - pad * 2);
  const scaleX = (minute) => pad + ((minute - minX) / spanX) * innerW;
  const scaleY = (viewers) => pad + innerH - (Math.max(0, viewers) / maxY) * innerH;
  const path = series
    .map((p, i) => `${i === 0 ? "M" : "L"}${scaleX(p.minute).toFixed(1)} ${scaleY(p.viewers).toFixed(1)}`)
    .join(" ");
  return { path, scaleX, scaleY, maxY, minX, maxX };
}
